import {toast} from '@wandb/weave/common/components/elements/Toast';
import {makeDidPropsOrStateChange} from '@wandb/weave/common/util/shouldUpdate';
import produce from 'immer';
import * as _ from 'lodash';
import memoize from 'memoize-one';
import React from 'react';
import {Redirect} from 'react-router-dom';

import BulkTagEditor, {TagState} from '../../components/Tags/BulkTagEditor';
import {combinedFilter} from '../../state/graphql/runSelectorQuery';
import {setCreateSweepArgs} from '../../state/sweeps/actions';
import * as Query from '../../util/queryts';
import * as QueryTypes from '../../util/queryTypes';
import {Config as TableSettings} from '../../util/runfeed';
import * as Run from '../../util/runs';
import * as RunTypes from '../../util/runTypes';
import * as TableCols from '../../util/tablecols';
import {WBTableRowWithRunSetInfo} from '../../util/tablecols';
import {CreateSweepButton} from '../CreateSweepButton';
import {DeleteRunsButton} from '../DeleteRunsButton';
import {JobDrawerLocked} from '../Launch/JobDrawer/JobDrawer';
import {ProjectPicker} from '../ProjectPicker/ProjectPicker';
import {RunMover} from '../RunMover';
import {RunsSearchBar} from '../RunsSearchBar';
import {
  type RunSelectorPropsHook,
  useRunSelectorProps,
} from './hooks/useRunSelectorProps';
import {MoveRunDrawerBulkButton} from './MoveRunDrawer/MoveRunDrawerBulk';
import {RunsAutoCols} from './RunsAutoCols';
import {RunsColumnEditor} from './RunsColumnEditor';
import {RunsDownloadMenu} from './RunsDownloadMenu';
import {RunSelectorCell} from './RunSelectorCell';
import {RunSelectorRowHeader} from './RunSelectorRowHeader';
import {RunsFreezeAndFilter} from './RunsFreeze';
import {RunsGroupTableAction} from './RunsGrouping';
import {RunsSortTableAction} from './RunsSort';
import {makeRunsSortIndicator} from './RunsSortIndicator';
import {RunsTableHeader} from './RunsTableHeader';
import type {RunSelectorInnerProps} from './types';
import type {WBTableColumn, WBTableProps} from './WBTable/types';
import {WBTable, WBTableComp} from './WBTable/WBTable';

/**
 * RunSelectorInner is WBTable, wrapped with GQL queries for project info,
 * tags, and runs. For runs data, there's a top-level query to populate the
 * table and separate sub-queries for each expanded group (see WBTableRow)
 * and CSV download (see TableExport).
 */
const FIXED_COLUMNS = ['run:displayName'];

type AllRunSelectorProps = RunSelectorInnerProps & RunSelectorPropsHook;

interface RunSelectorInnerState {
  tableMessage?: JSX.Element;
  creatingSweep: boolean;
  currentPage: number;
  sortPopupOpen: boolean;
}

class RunSelectorInnerComp extends React.Component<
  AllRunSelectorProps,
  RunSelectorInnerState
> {
  state: RunSelectorInnerState = {
    tableMessage: undefined,
    creatingSweep: false,
    currentPage: 1,
    sortPopupOpen: false,
  };

  didPropsOrStateChange = makeDidPropsOrStateChange({
    name: 'RunSelector',
    deep: [
      'runSelectorQuery',
      'sort',
      'runSetFull',
      'updateGroupRunTags',
      'runSet',
    ],
    debug: false,
    verbose: true,
  });

  tableRef = React.createRef<WBTableComp>();

  rows = memoize((rowData: RunTypes.Run[], selectedRunName?: string) => {
    return rowData.map(r => {
      return {
        ...r,
        selectedRunName,
        runsetInfo: {
          id: this.props.runSetFull.id,
          name: this.props.runSetFull.name,
        },
        __address__: TableCols.makeRowAddressComponent(
          r.name,
          this.props.runSetFull.id
        ),
      };
    });
  });

  lastRowCount = 0;

  renderCell: WBTableColumn['renderCell'] = (
    column,
    row: WBTableRowWithRunSetInfo,
    {
      addFilter,
      toggleExpandedRow,
      recursionDepth,
      isGroup,
      isGroupExpanded,
      loadingChildren,
      isHoveringRow,
    }
  ) => {
    if (
      Run.keysEqual(column.key, {
        section: 'run',
        name: 'displayName',
      })
    ) {
      return (
        <RunSelectorRowHeader
          benchmarkMeta={this.props.benchmarkMeta}
          customRunColorsRef={this.props.customRunColorsRef}
          customRunNamesRef={this.props.customRunNamesRef}
          disabledFeatures={this.props.disabledFeatures}
          disableRunLink={this.props.disableRunLinks}
          displayBenchmark={this.props.displayBenchmark}
          displayTempCheckbox={Boolean(isHoveringRow)}
          entityName={this.props.entityName}
          groupSelectionsRef={this.props.runSet.groupSelectionsRef}
          isGroup={isGroup}
          isGroupExpanded={Boolean(isGroupExpanded)}
          isInReport={this.props.isInReport}
          isSingleMode={this.props.isSingleMode}
          isTableCollapsed={this.props.isTableCollapsed}
          loadingChildren={loadingChildren}
          onSelectRunName={this.props.onSelectRunName}
          onTempCheckboxClick={this.clearTableMessage}
          projectName={this.props.projectName}
          recursionDepth={recursionDepth}
          row={row}
          runSetRef={this.props.runSetRef}
          selectedCount={this.props.tempSelectedCount}
          selectedRunName={this.props.selectedRunName}
          semanticLegendRef={this.props.semanticLegendRef}
          showArtifactCounts={this.props.showArtifactCounts}
          showLogCounts={this.props.showLogCounts}
          tempSelectionsRef={this.props.tempSelectionsRef}
          onGroupButtonClick={() => {
            if (isGroup) {
              return toggleExpandedRow && toggleExpandedRow(row.__address__);
            }
          }}
          saveRunName={this.saveRunName}
          stopRun={id =>
            this.props.stopRun({variables: {id}}).then(this.reload)
          }
          setLaunchDrawerRow={this.props.setLaunchDrawerRow}
          isLaunchActive={this.props.isLaunchActive}
          truncationType={this.props.truncationType}
        />
      );
    }

    const onAddTag = (tagName: string) => {
      if (isGroup) {
        this.addGroupTag(row, tagName);
      } else {
        this.addTag(row, tagName);
      }
    };

    const onDeleteTag = (tagName: string) => {
      if (isGroup) {
        this.deleteGroupTag(row, tagName);
      } else {
        this.deleteTag(row, tagName);
      }
    };

    const onSaveRowNotes = (newNotes: string) => {
      const isGroupRun = isGroup && row.group === row.name;
      if (isGroupRun) {
        this.saveRunGroupNotes(row, newNotes);
      } else {
        this.saveRunNotes(row, newNotes);
      }
    };

    return (
      <RunSelectorCell
        addFilter={addFilter}
        column={column}
        disabledFeatures={this.props.disabledFeatures || {}}
        disableRunLinks={Boolean(this.props.disableRunLinks)}
        entityName={this.props.entityName}
        isGroup={Boolean(isGroup)}
        isHoveringRow={Boolean(isHoveringRow)}
        onAddTag={onAddTag}
        onDeleteTag={onDeleteTag}
        onSaveRowNotes={onSaveRowNotes}
        projectName={this.props.projectName}
        row={row}
      />
    );
  };

  renderHeader = (displayedRows: RunTypes.Run[]) => {
    return (
      <RunsTableHeader
        isSingleMode={this.props.isSingleMode}
        totalRowCount={this.props.totalRuns}
        displayedRows={displayedRows}
        runSetRef={this.props.runSetRef}
        tempSelectionsRef={this.props.tempSelectionsRef}
        customRunNamesRef={this.props.customRunNamesRef}
        showTempSelection={!this.props.isExpandable || !!this.props.isExpanded}
        onlyShowSelected={this.props.tableSettings.onlyShowSelected}
        disabledFeatures={this.props.disabledFeatures}
        isInReport={this.props.isInReport}
        onAllItemsTempSelected={() => {
          // Show the "select all runs in project?" prompt.
          // TODO(views): This could maybe be factored out somehow.
          if (this.props.totalRuns > displayedRows.length) {
            const isGrouped = !_.isEmpty(this.props.groupSelections.grouping);
            this.setState({
              tableMessage: (
                <>
                  All <strong>{displayedRows.length}</strong>
                  {isGrouped ? ' groups ' : ' runs '} on this page are selected.{' '}
                  <span
                    className="fake-link"
                    onClick={() => {
                      this.props.tempSelectAll();
                      this.clearTableMessage();
                    }}>
                    Select all
                    {isGrouped ? ' groups ' : ' runs '} in this project
                  </span>
                </>
              ),
            });
          }
        }}
        onAllItemsTempUnselected={this.clearTableMessage}
        onVisibilitySelectNone={this.props.selectNone}
        onVisibilitySelectAll={this.props.selectAll}
        onSetOnlyShowSelected={newSetting => {
          this.props.setTableSettings({
            ...this.props.tableSettings,
            onlyShowSelected: newSetting,
          });
          this.reload();
        }}
      />
    );
  };

  unionKeys = () => {
    const {filteredKeys, defaultKeys, tableSettings} = this.props;
    const filteredKeyStrings = filteredKeys.map(k => Run.keyToString(k));
    const orderKeys = tableSettings.columnOrder
      .filter(k => !_.includes(filteredKeyStrings, k))
      .map(k => Run.keyFromString(k)!);

    return TableCols.unionKeys(defaultKeys, [...filteredKeys, ...orderKeys]);
  };

  column = (key: RunTypes.Key) => {
    const isDisplayNameCol = Run.keysEqual(key, {
      section: 'run',
      name: 'displayName',
    });

    return TableCols.keyToColumn(key, {
      renderHeader: isDisplayNameCol ? this.renderHeader : undefined,
      renderCell: this.renderCell,
    });
  };

  getExtraTableActionsLeft = memoize(
    (
      entityName,
      hasEntityStorageEnabled,
      projectName,
      enableSetProject,
      enableFreezeRunset,
      groupSelections,
      username,
      tempSelectNone,
      tags,
      disabledFeatures,
      tableSettings: TableSettings
    ) => (
      <>
        {enableSetProject && this.props.projectInternalId && (
          <ProjectPicker
            entityName={entityName}
            projectName={projectName}
            setProject={this.props.setProject}
            projectId={this.props.projectInternalId}
          />
        )}
        {!disabledFeatures?.search && (
          <div className={'runs-search-bar-wrapper'}>
            <RunsSearchBar
              isGroupSearch={Query.isGroupedByGroup(groupSelections.grouping)}
              isInReport={this.props.isInReport}
              search={{
                query: this.props.runSet.search.query,
                isRegex: this.props.runSet.search.isRegex ?? true,
              }}
              setSearch={(query, isRegex) => {
                if (this.tableRef.current) {
                  this.tableRef.current.resetPagination();
                }
                this.props.setSearch(query, isRegex);
              }}
              searchHistory={this.props.runSet.searchHistory}
              onAddToHistory={this.props.addToHistory}
            />
          </div>
        )}
        {this.props.isLaunchActive && (
          <JobDrawerLocked
            open={this.props.launchDrawerJobRun != null}
            closeDrawer={this.props.closeLaunchConfigModal}
            entityName={entityName}
            projectName={projectName}
            jobId={this.props.launchDrawerJobRun?.jobId ?? ''}
            jobVersion={this.props.launchDrawerJobRun?.jobVersion ?? ''}
            defaultRunToClone={this.props.launchDrawerJobRun?.runName}
          />
        )}
        <div style={{display: 'flex', gap: '8px'}}>
          {!disabledFeatures?.filter && (
            <RunsFreezeAndFilter
              enableFreezeRunset={enableFreezeRunset}
              filtersRef={this.props.runSet.filtersRef}
              entityName={entityName}
              isInReport={this.props.isInReport}
              projectName={projectName}
              username={username}
              compact={!!this.props.isExpandable && !this.props.isExpanded}
              onChange={() => {
                if (this.tableRef.current != null) {
                  this.tableRef.current.resetPagination();
                }
              }}
              tableSettings={tableSettings}
            />
          )}
          {!disabledFeatures?.group && (
            <RunsGroupTableAction
              entityName={entityName}
              projectName={projectName}
              groupSelectionsRef={this.props.runSet.groupSelectionsRef}
              recommendedGrouping={this.props.recommendedGrouping}
              compact={!!this.props.isExpandable && !this.props.isExpanded}
              isInReport={this.props.isInReport}
              onGroupingChanged={() => {
                this.tableRef.current?.resetPagination();
                tempSelectNone();
              }}
              tableSettings={tableSettings}
            />
          )}
          {!disabledFeatures?.sort && (
            <RunsSortTableAction
              entityName={entityName}
              projectName={projectName}
              sortRef={this.props.runSet.sortRef}
              compact={!!this.props.isExpandable && !this.props.isExpanded}
              isInReport={this.props.isInReport}
              open={this.state.sortPopupOpen}
              onOpen={() => this.setState({sortPopupOpen: true})}
              onClose={() => this.setState({sortPopupOpen: false})}
              onSortChanged={() =>
                this.tableRef.current && this.tableRef.current.resetPagination()
              }
              tableSettings={tableSettings}
            />
          )}
          {this.props.isExpandable && !this.props.isExpanded ? (
            <></>
          ) : (
            <>
              {/* BATCH TAGGING, MOVING, DELETING */}
              {!disabledFeatures?.tag && (
                <BulkTagEditor
                  // TODO(views): Here and below, this is a bad way to pass these counts through as it
                  // won't always update.
                  selectedCount={this.props.tempSelectedCount}
                  isInReport={this.props.isInReport}
                  tags={tags}
                  addTag={(tag: string) => this.bulkUpdateTag('add', tag)}
                  removeTag={(tag: string) => this.bulkUpdateTag('remove', tag)}
                />
              )}
              {!disabledFeatures?.move && this.props.tempSelectedCount ? (
                <MoveRunDrawerBulkButton
                  entityName={this.props.entityName}
                  groupTempSelection={this.props.groupTempSelection}
                  isInReport={Boolean(this.props.isInReport)}
                  onMovedRuns={() => {
                    this.props.tempSelectNone();
                    this.reload();
                  }}
                  projectName={this.props.projectName}
                  groupSelectionsRef={this.props.runSet.groupSelectionsRef}
                  selectedCount={this.props.tempSelectedCount}
                />
              ) : null}

              {!disabledFeatures?.move && this.props.projectInternalId && (
                <RunMover
                  entityName={entityName}
                  // prevent users from trying to move runs to another entity if the entity is using external storage
                  // because this operation does not move the run files
                  requireSameEntity={hasEntityStorageEnabled}
                  requireSameEntityReason={
                    "Can't move runs between entities when using external storage"
                  }
                  isInReport={this.props.isInReport}
                  projectInternalId={this.props.projectInternalId}
                  projectName={projectName}
                  selectedCount={this.props.tempSelectedCount}
                  refetch={() => this.reload()}
                  onMove={(entity, proj) => this.moveRuns(entity, proj)}
                />
              )}
              {!disabledFeatures?.delete && (
                <DeleteRunsButton
                  selectedCount={this.props.tempSelectedCount}
                  isInReport={this.props.isInReport}
                  onDelete={(deleteArtifacts: boolean) =>
                    this.deleteRuns(deleteArtifacts)
                  }
                  outputArtifactsCount={this.props.tempOutputArtifactsCount}
                  activeRunsCount={this.props.tempSelectedActiveCount}
                />
              )}
              {!disabledFeatures?.['create-sweep'] && (
                <CreateSweepButton onClick={this.createSweepWithPriorRuns} />
              )}
            </>
          )}
        </div>
      </>
    )
  );

  getExtraTableActionsRight = memoize(
    (allQueryVariables, allColumns, tableSettings, currentPage) => {
      return (
        <>
          {!this.props.disabledFeatures?.export && (
            <RunsDownloadMenu
              pageQuery={allQueryVariables}
              tableSettings={tableSettings}
            />
          )}
          {!this.props.disabledFeatures?.['auto-cols'] && (
            <RunsAutoCols
              query={allQueryVariables}
              tableSettings={tableSettings}
              isInReport={this.props.isInReport}
              setTableSettings={this.props.setTableSettings}
              currentPage={currentPage}
              columns={allColumns}
            />
          )}
        </>
      );
    }
  );

  saveRunNotes = _.debounce((run: RunTypes.Run, newNotes: string) => {
    const currentNotes = run.notes;
    if (newNotes !== currentNotes) {
      // Sending undefined values for displayName or notes clears them, so
      // we always need to send both.
      this.props.updateRun(run.id, run.name, {
        displayName: run.displayName,
        notes: newNotes,
      });
    }
  }, 500);

  saveRunGroupNotes = _.debounce((run: RunTypes.Run, newNotes: string) => {
    const {group: name, tags} = run;
    const {entityName, projectName} = this.props;
    this.props.updateGroupRun({
      variables: {
        projectName,
        entityName,
        name,
        tags: tags.map(t => t.name),
        notes: newNotes,
      },
    });
  }, 500);

  updateSort = (updateFn: (sort: QueryTypes.Sort) => void) => {
    const {sort, setSort} = this.props;
    const newSort = produce(sort, updateFn);
    setSort(newSort);
  };

  clearTableMessage = (callback?: () => void) => {
    this.setState({tableMessage: undefined}, () => {
      if (callback) {
        callback();
      }
    });
  };

  shouldComponentUpdate(
    nextProps: RunSelectorInnerProps,
    nextState: RunSelectorInnerState
  ): boolean {
    return this.didPropsOrStateChange(
      [this.props, nextProps],
      [this.state, nextState]
    );
  }

  saveRunName = (run: RunTypes.Run, newName: string) => {
    const currentName = run.displayName || run.name;
    if (newName !== currentName) {
      // Sending undefined values for displayName or notes clears them, so
      // we always need to send both.
      this.props.updateRun(run.id, run.name, {
        displayName: newName,
        notes: run.notes,
      });
      window.analytics?.track('Run name edited');
    }
  };

  addTag = (run: RunTypes.Run, newTagName: string) => {
    const currentTagNames = run.tags.map(t => t.name);
    if (!currentTagNames.includes(newTagName)) {
      this.props.updateRunTags(run, [...currentTagNames, newTagName]);
    }
  };

  addGroupTag = (run: RunTypes.Run, newTagName: string) => {
    const currentTagNames = run.tags.map(t => t.name);
    if (currentTagNames.includes(newTagName)) {
      return;
    }

    this.props.updateGroupRunTags(run, [...currentTagNames, newTagName]);
  };

  deleteTag = (run: RunTypes.Run, removeTagName: string) => {
    const currentTagNames = run.tags.map(t => t.name);
    if (_.includes(currentTagNames, removeTagName)) {
      this.props.updateRunTags(
        run,
        currentTagNames.filter(t => t !== removeTagName)
      );
    }
  };

  deleteGroupTag = (run: RunTypes.Run, removeTagName: string) => {
    const currentTagNames = run.tags.map(t => t.name);
    if (!currentTagNames.includes(removeTagName)) {
      return;
    }
    this.props.updateGroupRunTags(
      run,
      currentTagNames.filter(t => t !== removeTagName)
    );
  };

  bulkUpdateTag(actionType: 'add' | 'remove', tagName: string): void {
    const {entityName, projectName} = this.props;
    const queryFilters = this.combinedFilter();
    this.props
      .bulkUpdateRunTags({
        entityName,
        projectName,
        filters: JSON.stringify(queryFilters),
        [actionType === 'add' ? 'addTags' : 'removeTags']: [tagName],
      })
      .then(() => {
        // Refreshes the <BulkTagEditor> menu
        if (
          !this.props.bulkEditorProjectTagsQuery.loading &&
          this.props.bulkEditorProjectTagsQuery.refetch
        ) {
          this.props.bulkEditorProjectTagsQuery.refetch();
        }
      });
  }

  moveRuns(
    destinationEntityName: string,
    destinationProjectName: string
  ): Promise<string> {
    const {entityName, projectName} = this.props;
    const queryFilters = this.combinedFilter();

    return this.props
      .moveRuns({
        variables: {
          sourceEntityName: entityName,
          sourceProjectName: projectName,
          destinationEntityName,
          destinationProjectName,
          filters: JSON.stringify(queryFilters),
        },
      })
      .then(response => {
        if (response.data == null || response.data.moveRuns == null) {
          throw new Error('Unexpected response');
        }
        return response.data.moveRuns.task.id;
      });
  }

  deleteRuns(deleteArtifacts: boolean): void {
    const {entityName, projectName} = this.props;
    const queryFilters = this.combinedFilter();

    this.props
      .deleteRuns({
        variables: {
          entityName,
          projectName,
          filters: JSON.stringify(queryFilters),
          deleteArtifacts,
        },
      })
      .then(resp => {
        if (resp.data?.deleteRuns?.errors) {
          toast(resp.data.deleteRuns.errors, {type: 'error', autoClose: 2500});
        } else {
          toast('Runs deleted', {type: 'success', autoClose: 2500});
        }
        this.reload();
      })
      .catch(err => {
        let errorMessage = 'Unable to delete runs.';
        if (err?.networkError?.response?.status === 403) {
          errorMessage = 'Unable to delete runs. Invalid permissions.';
        }

        toast(errorMessage, {type: 'error', autoClose: 2500});
      });
  }

  createSweepWithPriorRuns = () => this.setState({creatingSweep: true});

  combinedFilter = () => {
    const {filters, mergeFilters, runSet, customRunNames} = this.props;
    const groupTempSelection = this.props.groupTempSelection;

    return combinedFilter(
      filters,
      groupTempSelection,
      undefined,
      mergeFilters,
      runSet.search,
      customRunNames
    );
  };

  reload = () => {
    if (
      !this.props.runSelectorQuery.loading &&
      this.props.runSelectorQuery.refetch
    ) {
      this.props.runSelectorQuery.refetch();
    }
    // Refreshes the <BulkTagEditor> menu
    if (
      !this.props.bulkEditorProjectTagsQuery.loading &&
      this.props.bulkEditorProjectTagsQuery.refetch
    ) {
      this.props.bulkEditorProjectTagsQuery.refetch();
    }
  };

  columnEditor = (
    config: TableSettings,
    setTableSettings: (config: TableSettings) => void
  ) => {
    const {entityName, projectName} = this.props;
    return (
      <RunsColumnEditor
        entityName={entityName}
        projectName={projectName}
        config={config}
        fixedColumns={FIXED_COLUMNS}
        update={setTableSettings}
      />
    );
  };

  onSetCurrentPage = (currentPage: number) => {
    this.setState({currentPage});
  };

  openSortPopup = () => this.setState({sortPopupOpen: true});

  componentDidUpdate() {
    const {entityName, projectName, runSetFull, tempSelections} = this.props;
    const {creatingSweep} = this.state;

    if (creatingSweep) {
      window.analytics?.track('Create Sweep Clicked', {
        entityName,
        projectName,
        location: 'runs table',
      });
      this.props.dispatch(
        setCreateSweepArgs({
          runSet: runSetFull,
          tempSelections,
        })
      );
    }
  }

  render() {
    const {
      disabledFeatures,
      enableFreezeRunset,
      enableSetProject,
      entityName,
      groupSelections,
      isLoadingRuns,
      isLoadingRunsPolled,
      loadMoreRuns,
      projectName,
      onHeightChange,
      runs,
      selectedRunName,
      setGrouping,
      tableSettings,
      tempSelectNone,
      topLevelQueryVariables,
      totalRuns,
    } = this.props;
    const {tableMessage, creatingSweep, currentPage} = this.state;

    if (creatingSweep) {
      return (
        <Redirect to={`/${entityName}/${projectName}/create-sweep`} push />
      );
    }

    const runSelectorQuery = this.props.runSelectorQuery;
    const username = this.props.viewer ? this.props.viewer.username : '';

    const project = runSelectorQuery.initialLoading
      ? undefined
      : runSelectorQuery?.project;
    const hasEntityStorageEnabled =
      project?.entity.settings?.storageBucketInfo != null;

    let tags: TagState[] = [];
    const bulkTagsProject = this.props.bulkEditorProjectTagsQuery.initialLoading
      ? undefined
      : this.props.bulkEditorProjectTagsQuery.data?.project;
    if (bulkTagsProject) {
      const counts: {[key: string]: number} = {};
      (bulkTagsProject.tagCounts || []).forEach(t => {
        counts[t.name] = t.count;
      });
      tags = (bulkTagsProject.tags || []).map(tag => ({
        name: tag.name,
        colorIndex: tag.colorIndex,
        count: counts[tag.name] || 0,
      }));
    }

    const rows = this.rows(runs, selectedRunName);
    const totalRowCount = totalRuns;
    const allColumns = this.unionKeys().map(this.column);

    if (rows.length !== this.lastRowCount) {
      this.lastRowCount = rows.length;
      onHeightChange?.();
    }

    const shouldShowTableLoading =
      (runSelectorQuery.initialLoading || isLoadingRuns) &&
      !isLoadingRunsPolled;

    const tableProps: WBTableProps = {
      className:
        'run-selector' +
        (this.props.className ? ` ${this.props.className}` : '') +
        (this.props.isExpandable ? ` expandable` : ''),
      clearTableMessage: this.clearTableMessage,
      columns: allColumns,
      disabledFeatures: this.props.disabledFeatures,
      expandable: this.props.isExpandable,
      expanded: this.props.isExpanded,
      expandedRowAddresses: groupSelections.expandedRowAddresses,
      filters: this.props.filters,
      fixedColumns: FIXED_COLUMNS,
      grouping: groupSelections.grouping,
      isInReport: this.props.isInReport,
      isSingleMode: this.props.isSingleMode,
      loading: shouldShowTableLoading,
      onSetExpanded: this.props.onSetExpanded,
      rows,
      setFilters: this.props.setFilters,
      setGrouping,
      setTableSettings: this.props.setTableSettings,
      showArtifactCounts: this.props.showArtifactCounts,
      showLogCounts: this.props.showLogCounts,
      SortIndicatorComponent: makeRunsSortIndicator(this.props.runSet.sortRef),
      tableLoadMore: loadMoreRuns,
      tableMessage,
      tableSettings,
      title: this.props.title,
      toggleExpandedRow: this.props.toggleExpandedRow,
      totalRowCount,
      updateSort: this.updateSort,
      updateTruncationType: this.props.setTruncationType,
      truncationType: this.props.truncationType,
      extraTableActionsLeft: this.getExtraTableActionsLeft(
        entityName,
        hasEntityStorageEnabled,
        projectName,
        enableSetProject,
        enableFreezeRunset,
        groupSelections,
        username,
        tempSelectNone,
        tags,
        disabledFeatures,
        tableSettings
      ),
      extraTableActionsRight: this.getExtraTableActionsRight(
        topLevelQueryVariables,
        allColumns,
        tableSettings,
        currentPage
      ),
      columnEditor: this.columnEditor,
      onSetCurrentPage: this.onSetCurrentPage,
      openSortPopup: this.openSortPopup,
      topLevelQueryVariables,
      sort: this.props.sort,
    };

    return <WBTable tableRef={this.tableRef} {...tableProps} />;
  }
}

export const RunSelectorInner = (props: RunSelectorInnerProps) => {
  const selectedProps = useRunSelectorProps(props);
  return <RunSelectorInnerComp {...props} {...selectedProps} />;
};
